import React from 'react';
import { FileText } from 'lucide-react';
import FeeActions from './FeeActions';

const FeeTableRow = ({
  student,
  fee,
  isSelected = false,
  onToggleSelect,
  onAlertParent,
  onCollectPayment,
  onEditFee,
  onViewHistory,
  onViewStudentDrawer,
  onGenerateClearanceCert
}) => {
  const isVoided = fee?.status === 'VOIDED';

  // Calculate authoritative financials
  const total = fee?.totalFees != null
    ? Number(fee.totalFees)
    : ((fee?.items && fee.items.length > 0)
        ? fee.items.reduce((s, i) => s + (i.netAmount || i.originalAmount || 0), 0)
        : null);

  const paid = fee?.paidFees != null ? Number(fee.paidFees) : (fee ? 0 : null);
  const concession = fee?.concessionAmount != null ? Number(fee.concessionAmount) : 0;
  const penalty = fee?.penaltyAmount != null ? Number(fee.penaltyAmount) : 0;
  const due = fee?.pendingFee != null
    ? Number(fee.pendingFee)
    : (total != null ? Math.max(0, total - concession + penalty - (paid || 0)) : null);

  // Edit history diff details
  const latestEdit = fee?.editHistory && fee.editHistory.length > 0 ? fee.editHistory[0] : null;
  const oldTotal = latestEdit?.oldTotalFees != null
    ? Number(latestEdit.oldTotalFees)
    : (fee?.previousTotalFees != null ? Number(fee.previousTotalFees) : null);
  const newTotal = latestEdit?.newTotalFees != null
    ? Number(latestEdit.newTotalFees)
    : (fee?.totalFees != null ? Number(fee.totalFees) : null);

  const hasTotalChanged = Boolean(fee?.lastModifiedAt) && oldTotal != null && newTotal != null && oldTotal !== newTotal && oldTotal > 0;
  const totalDiff = hasTotalChanged ? newTotal - oldTotal : 0;

  const rawDiffSummary = latestEdit?.changesSummary || fee?.lastModifiedChangesSummary || (hasTotalChanged ? `Total Fee: ₹${oldTotal.toLocaleString('en-IN')} → ₹${newTotal.toLocaleString('en-IN')}` : '');
  const diffSummaryText = (fee?.lastModifiedAt && rawDiffSummary && !rawDiffSummary.toLowerCase().includes('initial fee')) ? rawDiffSummary : '';

  // Determine status color & label
  let statusText = 'NOT ASSIGNED';
  let statusBg = '#f1f5f9';
  let statusColor = '#64748b';

  if (isVoided) {
    statusText = 'VOIDED';
    statusBg = '#fef2f2';
    statusColor = '#991b1b';
  } else if (fee && (fee.feeStructureId || (fee.totalFees != null && fee.totalFees > 0) || fee.feeStructureName)) {
    const isOverdue = due > 0 && fee.installments?.some(inst => inst.status !== 'PAID' && new Date(inst.dueDate) < new Date());
    if (isOverdue) {
      statusText = 'OVERDUE';
      statusBg = '#fef2f2';
      statusColor = '#dc2626';
    } else if (due === 0 || fee.status === 'PAID') {
      statusText = 'PAID';
      statusBg = '#f0fdf4';
      statusColor = '#166534';
    } else if (paid > 0) {
      statusText = 'PARTIAL';
      statusBg = '#eff6ff';
      statusColor = '#1d4ed8';
    } else {
      statusText = 'UNPAID';
      statusBg = '#fff7ed';
      statusColor = '#c2410c';
    }
  }

  return (
    <tr
      style={{
        borderBottom: '1px solid #e2e8f0',
        backgroundColor: isSelected ? '#eff6ff' : (isVoided ? '#fafafa' : '#ffffff'),
        transition: 'background-color 0.15s ease'
      }}
      className="fee-table-row"
    >
      {/* 1. Selection Checkbox */}
      <td style={{ padding: '6px 8px', textAlign: 'center', verticalAlign: 'middle', width: '38px' }}>
        <input
          type="checkbox"
          checked={isSelected}
          onChange={() => onToggleSelect(student.studentId)}
          style={{ cursor: 'pointer', width: '14px', height: '14px', borderRadius: '3px' }}
        />
      </td>

      {/* 2. Student Information */}
      <td style={{ padding: '6px 10px', verticalAlign: 'middle', minWidth: '180px' }}>
        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '4px', lineHeight: '1.25' }}>
          <span
            onClick={() => onViewStudentDrawer(student, fee)}
            style={{
              fontWeight: '700',
              color: isVoided ? '#64748b' : '#0f172a',
              fontSize: '12.5px',
              cursor: 'pointer',
              textDecoration: 'none'
            }}
            onMouseEnter={(e) => {
              if (!isVoided) e.currentTarget.style.color = '#2563eb';
            }}
            onMouseLeave={(e) => {
              if (!isVoided) e.currentTarget.style.color = '#0f172a';
            }}
            title="Click to view complete student fee breakdown & payment history"
          >
            {student.name}
          </span>

          <span style={{ fontSize: '11px', color: '#64748b', fontWeight: '500' }}>
            ({student.studentId})
          </span>

          {isVoided && (
            <span
              style={{
                fontSize: '9.5px',
                background: '#fee2e2',
                color: '#991b1b',
                padding: '1px 5px',
                borderRadius: '3px',
                fontWeight: '700',
                lineHeight: '1.2'
              }}
            >
              VOIDED
            </span>
          )}

          {fee?.lastModifiedAt && diffSummaryText && !isVoided && (
            <span
              title={`✏️ Corrected by ${fee.lastModifiedByName || fee.lastModifiedBy || 'Admin'} on ${new Date(fee.lastModifiedAt).toLocaleDateString('en-IN')}: ${diffSummaryText.split('\n')[0]}`}
              style={{
                fontSize: '9.5px',
                background: '#eff6ff',
                color: '#1d4ed8',
                border: '1px solid #bfdbfe',
                padding: '0 4px',
                borderRadius: '3px',
                fontWeight: '700',
                cursor: 'help',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '2px',
                lineHeight: '1.3'
              }}
            >
              ✏️ Corrected
            </span>
          )}
        </div>
      </td>

      {/* 3. Class / Section */}
      <td style={{ padding: '6px 10px', color: '#334155', fontSize: '12px', whiteSpace: 'nowrap', verticalAlign: 'middle', minWidth: '100px' }}>
        {student.className || '—'} {student.section ? `(${student.section})` : ''}
      </td>

      {/* 4. Assigned Structure */}
      <td
        style={{
          padding: '6px 10px',
          color: '#0f172a',
          fontSize: '12px',
          verticalAlign: 'middle',
          maxWidth: '130px',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}
        title={fee?.feeStructureName || ''}
      >
        {fee?.feeStructureName || (fee?.type && fee?.type !== 'tuition' ? fee.type : null) || (
          <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>— Not Assigned —</span>
        )}
      </td>

      {/* 5. Total Fee */}
      <td style={{ padding: '6px 10px', textAlign: 'right', fontWeight: '600', color: isVoided ? '#94a3b8' : '#0f172a', fontSize: '12.5px', whiteSpace: 'nowrap', verticalAlign: 'middle', minWidth: '95px' }}>
        {isVoided ? (
          <span style={{ textDecoration: 'line-through' }}>₹{(oldTotal || total || 0).toLocaleString('en-IN')}</span>
        ) : (
          <div style={{ display: 'inline-flex', alignItems: 'baseline', gap: '3px', justifyContent: 'flex-end' }}>
            <span>{total != null ? `₹${total.toLocaleString('en-IN')}` : '—'}</span>
            {hasTotalChanged && (
              <span style={{ fontSize: '10px', color: totalDiff > 0 ? '#dc2626' : '#166534', fontWeight: '700' }}>
                ({totalDiff > 0 ? '+' : ''}₹{totalDiff.toLocaleString('en-IN')})
              </span>
            )}
          </div>
        )}
      </td>

      {/* 6. Paid Amount */}
      <td style={{ padding: '6px 10px', textAlign: 'right', color: isVoided ? '#94a3b8' : '#166534', fontWeight: '600', fontSize: '12.5px', whiteSpace: 'nowrap', verticalAlign: 'middle', minWidth: '90px' }}>
        {isVoided ? '₹0' : (paid != null ? `₹${paid.toLocaleString('en-IN')}` : '—')}
      </td>

      {/* 7. Outstanding Amount */}
      <td
        style={{
          padding: '6px 10px',
          textAlign: 'right',
          color: isVoided ? '#94a3b8' : (due > 0 ? '#b91c1c' : '#166534'),
          fontWeight: '700',
          fontSize: '12.5px',
          whiteSpace: 'nowrap',
          verticalAlign: 'middle',
          minWidth: '95px'
        }}
      >
        {isVoided ? '₹0' : (due != null ? `₹${due.toLocaleString('en-IN')}` : '—')}
      </td>

      {/* 8. Status Badge */}
      <td style={{ padding: '6px 10px', textAlign: 'center', whiteSpace: 'nowrap', verticalAlign: 'middle', minWidth: '90px' }}>
        <span
          style={{
            background: statusBg,
            color: statusColor,
            padding: '2px 6px',
            borderRadius: '4px',
            fontSize: '10px',
            fontWeight: '700',
            letterSpacing: '0.2px'
          }}
        >
          {statusText}
        </span>
      </td>

      {/* 9. Payment History */}
      <td style={{ padding: '6px 10px', textAlign: 'center', whiteSpace: 'nowrap', verticalAlign: 'middle', minWidth: '95px' }}>
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary"
          onClick={() => onViewHistory(student)}
          style={{
            fontSize: '11px',
            padding: '2px 7px',
            borderRadius: '4px',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '3px',
            borderColor: '#cbd5e1',
            color: '#475569',
            fontWeight: '600',
            backgroundColor: '#ffffff',
            height: '24px',
            lineHeight: 1
          }}
          title="View Receipt & Payment History Logs"
        >
          <FileText size={12} /> History
        </button>
      </td>

      {/* 10. Actions */}
      <td style={{ padding: '6px 10px', textAlign: 'center', whiteSpace: 'nowrap', verticalAlign: 'middle', minWidth: '150px' }}>
        <FeeActions
          student={student}
          fee={fee}
          isVoided={isVoided}
          due={due || 0}
          onAlertParent={onAlertParent}
          onCollectPayment={onCollectPayment}
          onEditFee={onEditFee}
          onViewHistory={onViewHistory}
          onViewStudentDrawer={onViewStudentDrawer}
          onGenerateClearanceCert={onGenerateClearanceCert}
        />
      </td>
    </tr>
  );
};

export default FeeTableRow;
